// The browser's Google Maps key (BL-018, maps package 4), as /auth/session hands it to the signed-in
// office (mapsKey): ui-web's GoogleMap loads Google's map with it, and draws its stand-in without one.
// Without one, the session says why (mapsOff, QA-756): the company has maps off, which the stand-in
// says, with a link to turn them on for someone who may change company settings; or the server has none.
// Set at sign-in from the session, cleared at sign-out; never stored. A screen draws a map as
// <GoogleMap mapsKey={useMapsKey()} theme={useShownTheme()} … />, or an address as AddressOnMap.
import { useSyncExternalStore } from 'react';
import type { MapsLoader } from '@flowology/ui-web';

export type MapsOff = 'company' | 'server';

/** The session's maps: its key, why there's none, and whether this person may turn the maps on. */
export interface MapsState {
  key: string | null;
  off: MapsOff | null;
  mayTurnOn: boolean;
}

const NONE: MapsState = { key: null, off: null, mayTurnOn: false };
let state: MapsState = NONE;
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** From the session: its key, why there's none (mapsOff), and whether they hold settings.edit. */
export function setMapsKey(next: string | null | undefined, off?: MapsOff | null, mayTurnOn = false): void {
  const key = next?.trim() || null;
  const why = key ? null : (off ?? null);
  if (key === state.key && why === state.off && mayTurnOn === state.mayTurnOn) return;
  state = { key, off: why, mayTurnOn };
  for (const l of listeners) l();
}

/** Whether the session's person may turn the maps on: they change company settings (settings.edit). */
export const mayTurnOnMaps = (s: { principal: { permissions?: readonly string[] } } | null | undefined) =>
  s?.principal.permissions?.includes('settings.edit') === true;

export const mapsKey = () => state.key;
export const mapsState = () => state;

/** The browser's maps key, or null for the stand-in; follows a new session. */
export function useMapsKey(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => state.key,
    () => null,
  );
}

/** The session's maps, the key and why there's none; follows a new session. */
export function useMaps(): MapsState {
  // the office draws only in the browser; a test's static render reads the session's maps the same way
  return useSyncExternalStore(subscribe, mapsState, mapsState);
}

// The loader GoogleMap uses: the page's (undefined), or none (null) where nothing may call Google
// (Storybook's page stories): GoogleMap then draws its stand-in even with a key.
let loader: MapsLoader | null | undefined;

export function setMapsLoader(next: MapsLoader | null | undefined): void {
  loader = next;
}

/** The loader for a screen's GoogleMap: undefined for the page's own. */
export const mapsLoader = (): MapsLoader | null | undefined => loader;
